import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Menu,
  useTheme,
  Container,
  InputAdornment,
  Switch,
  FormControlLabel,
  LinearProgress,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Autocomplete,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  VisibilityOutlined as ImpersonateIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';
import { useAccount } from '../contexts/AccountContext';
import {
  createUser,
  CreateUserRequest,
  getAvailableAccounts,
  AccountOption,
  addUserToAccount,
  removeUserFromAccount,
  updateUserAccountRole,
  updateAccount,
  UpdateAccountRequest
} from '../api/userManagement';
import { API_BASE_URL } from '../config/api';

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
  last_sign_in_at?: string;
  is_active: boolean;
  role: 'admin' | 'user';
  accounts: {
    account_id: string;
    account_name: string;
    role: 'owner' | 'admin' | 'member';
  }[];
}

interface UserWithEmail {
  id: string;
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  is_active?: boolean | null;
  created_at?: string | null;
  last_sign_in_at?: string | null;
}

interface Account {
  id: string;
  name: string;
  plan_type: 'starter' | 'essentials' | 'pro' | 'enterprise';
  plan_status?: 'active' | 'canceled' | 'past_due' | 'trialing';
  created_at: string;
  user_count: number;
  is_active: boolean;
  company_count?: number;
  freight_record_count?: number;
}

const SiteAdminDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tab } = useParams();
  const { startUserImpersonation } = useAccount();
  const activeTab = tab === 'accounts' ? 1 : 0; // Default to users (0), accounts (1)

  const [users, setUsers] = useState<User[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Search and filters
  const [userSearch, setUserSearch] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialog states
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [createAccountDialog, setCreateAccountDialog] = useState(false);
  const [manageAccountDialog, setManageAccountDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  // Edit form states
  const [editUserForm, setEditUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    is_active: true,
  });

  // Create user form states
  const [createUserForm, setCreateUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    password: '',
    send_invitation: true,
    account_id: '',
    account_role: 'member' as 'owner' | 'admin' | 'member',
  });
  const [createUserSubmitting, setCreateUserSubmitting] = useState(false);
  const [availableAccountsForUser, setAvailableAccountsForUser] = useState<AccountOption[]>([]);

  // Account creation workflow states
  const [createAccountStep, setCreateAccountStep] = useState(0);
  const [newAccountForm, setNewAccountForm] = useState({
    name: '',
    plan_type: 'starter' as 'starter' | 'essentials' | 'pro' | 'enterprise',
    owner_selection: 'existing' as 'existing' | 'new',
    existing_user_id: '',
    new_user: {
      first_name: '',
      last_name: '',
      email: '',
    }
  });
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  // Add user to account dialog states
  const [addUserToAccountDialog, setAddUserToAccountDialog] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    selectedUserId: '',
    selectedAccountId: '',
    accountRole: 'member' as 'owner' | 'admin' | 'member',
  });
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);

  // Edit account form states
  const [editAccountMode, setEditAccountMode] = useState(false);
  const [editAccountForm, setEditAccountForm] = useState({
    name: '',
    plan_type: 'starter' as 'starter' | 'essentials' | 'pro' | 'enterprise',
    plan_status: 'active' as 'active' | 'canceled' | 'past_due' | 'trialing',
    is_active: true,
  });
  const [editAccountSubmitting, setEditAccountSubmitting] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const tabPath = newValue === 0 ? 'users' : 'accounts';
    navigate(`/admin/${tabPath}`);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Check for OAuth success callback from impersonation Gmail connection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');
    const impersonated = urlParams.get('impersonated');

    if (connected === 'gmail' && impersonated === 'true') {
      setSuccess('Gmail integration connected successfully for the impersonated user!');

      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('connected');
      newUrl.searchParams.delete('impersonated');
      window.history.replaceState(null, '', newUrl.toString());
    }
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, userSearch, roleFilter, statusFilter]);

  useEffect(() => {
    filterAccounts();
  }, [accounts, accountSearch, statusFilter]);

  const checkAdminAccess = async () => {
    try {
      const { data, error } = await supabase.rpc('is_admin');

      if (error) throw error;

      if (!data) {
        setError('Access denied: Admin privileges required');
        return false;
      }

      return true;
    } catch (err: any) {
      console.error('Error checking admin access:', err);
      setError('Unable to verify admin access');
      return false;
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const hasAccess = await checkAdminAccess();
      if (!hasAccess) {
        setLoading(false);
        return;
      }

      await Promise.all([
        loadUsers(),
        loadAccounts(),
      ]);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const { data: usersData, error: usersError } = await supabase.rpc('get_users_with_emails');

      if (usersError) {
        console.error('Admin function error:', usersError);
        throw usersError;
      }

      const { data: accountUsersData, error: accountUsersError } = await supabase
        .from('account_users')
        .select(`
          user_id,
          account_id,
          role,
          joined_at,
          accounts (
            id,
            name
          )
        `);

      if (accountUsersError) {
        console.error('Account users error:', accountUsersError);
        throw accountUsersError;
      }

      const combinedUsers: User[] = (usersData as UserWithEmail[]).map(userData => {
        const userAccounts = accountUsersData
          ?.filter(au => au.user_id === userData.id)
          ?.map(au => ({
            account_id: au.account_id,
            account_name: (au.accounts as any)?.name || 'Unknown',
            role: au.role,
          })) || [];

        return {
          id: userData.id,
          email: userData.email || 'No email',
          first_name: userData.first_name || undefined,
          last_name: userData.last_name || undefined,
          created_at: userData.created_at || new Date().toISOString(),
          last_sign_in_at: userData.last_sign_in_at || undefined,
          is_active: userData.is_active !== false,
          role: (userData.role as 'admin' | 'user') || 'user',
          accounts: userAccounts,
        };
      });

      setUsers(combinedUsers);
      // Set available users for account assignment (filter out admins)
      setAvailableUsers(combinedUsers.filter(u => u.role !== 'admin'));
    } catch (err) {
      console.error('Error loading users:', err);
      throw err;
    }
  };

  const loadAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      // Use backend API with statistics
      const response = await fetch(`${API_BASE_URL}/admin/accounts?currentUserId=${user.id}&includeStats=true`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch accounts');
      }

      const { accounts: accountsData } = await response.json();

      const formattedAccounts = accountsData.map((account: any) => ({
        id: account.id,
        name: account.name,
        plan_type: account.plan_type,
        created_at: account.created_at,
        user_count: account.user_count || 0,
        is_active: account.is_active !== false,
        company_count: account.company_count || 0,
        freight_record_count: account.freight_record_count || 0,
      }));

      setAccounts(formattedAccounts);
    } catch (err) {
      console.error('Error loading accounts:', err);
      throw err;
    }
  };

  const filterUsers = () => {
    let filtered = users;

    if (userSearch) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(userSearch.toLowerCase())
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user =>
        statusFilter === 'active' ? user.is_active : !user.is_active
      );
    }

    setFilteredUsers(filtered);
  };

  const filterAccounts = () => {
    let filtered = accounts;

    if (accountSearch) {
      filtered = filtered.filter(account =>
        account.name.toLowerCase().includes(accountSearch.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(account =>
        statusFilter === 'active' ? account.is_active : !account.is_active
      );
    }

    setFilteredAccounts(filtered);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditUserForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email,
      role: user.role,
      is_active: user.is_active,
    });
    setEditUserDialog(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    try {
      const { data, error } = await supabase.rpc('admin_update_user_profile', {
        target_user_id: selectedUser.id,
        new_first_name: editUserForm.first_name || null,
        new_last_name: editUserForm.last_name || null,
        new_role: editUserForm.role,
        new_is_active: editUserForm.is_active,
      });

      if (error) throw error;

      if (!data) {
        throw new Error('Failed to update user profile');
      }

      setSuccess('User updated successfully');
      setEditUserDialog(false);
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const { data, error } = await supabase.rpc('admin_update_user_profile', {
        target_user_id: user.id,
        new_first_name: null,
        new_last_name: null,
        new_role: null,
        new_is_active: !user.is_active,
      });

      if (error) throw error;

      if (!data) {
        throw new Error('Failed to update user status');
      }

      setSuccess(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const handleImpersonateUser = async (user: User) => {
    try {
      // Don't allow impersonating site admins
      if (user.role === 'admin') {
        setError('Cannot impersonate other site administrators');
        return;
      }

      const userName = user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.email.split('@')[0];

      await startUserImpersonation(user.id, user.email, userName, user.role);

      setSuccess(`Now impersonating ${user.email}. You can navigate to any page to see their view.`);

      // Navigate to dashboard after a brief delay to show the success message
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err: any) {
      setError(err.message || 'Failed to start user impersonation');
    }
  };

  const handleCreateAccountNext = () => {
    setCreateAccountStep(createAccountStep + 1);
  };

  const handleCreateAccountBack = () => {
    setCreateAccountStep(createAccountStep - 1);
  };

  const handleCreateAccountComplete = async () => {
    try {
      // First create the account
      const { data: accountData, error: accountError } = await supabase.rpc('admin_create_account', {
        account_name: newAccountForm.name,
        account_plan_type: newAccountForm.plan_type,
      });

      if (accountError) throw accountError;

      const newAccountId = accountData;

      // Then handle the owner assignment
      let ownerId = newAccountForm.existing_user_id;

      if (newAccountForm.owner_selection === 'new') {
        // Create new user first (this would need to be implemented)
        // For now, we'll show an error since user creation via admin isn't implemented yet
        throw new Error('Creating new users not yet implemented. Please use an existing user.');
      }

      // Assign the owner to the account
      const { error: inviteError } = await supabase.rpc('admin_invite_user_to_account', {
        account_uuid: newAccountId,
        user_email: availableUsers.find(u => u.id === ownerId)?.email,
        user_role: 'owner',
      });

      if (inviteError) throw inviteError;

      setSuccess(`Account "${newAccountForm.name}" created successfully with owner assigned`);
      setCreateAccountDialog(false);
      setCreateAccountStep(0);
      setNewAccountForm({
        name: '',
        plan_type: 'starter',
        owner_selection: 'existing',
        existing_user_id: '',
        new_user: { first_name: '', last_name: '', email: '' }
      });
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    }
  };

  const handleManageAccount = (account: Account) => {
    setSelectedAccount(account);
    setEditAccountMode(false);
    setEditAccountForm({
      name: account.name,
      plan_type: account.plan_type,
      plan_status: account.plan_status || 'active',
      is_active: account.is_active,
    });
    setManageAccountDialog(true);
  };

  const handleEditAccount = () => {
    setEditAccountMode(true);
  };

  const handleCancelEditAccount = () => {
    if (selectedAccount) {
      setEditAccountForm({
        name: selectedAccount.name,
        plan_type: selectedAccount.plan_type,
        plan_status: selectedAccount.plan_status || 'active',
        is_active: selectedAccount.is_active,
      });
    }
    setEditAccountMode(false);
  };

  const handleSaveAccount = async () => {
    if (!selectedAccount) return;

    setEditAccountSubmitting(true);
    try {
      const updates: UpdateAccountRequest = {};

      // Only send changed fields
      if (editAccountForm.name !== selectedAccount.name) {
        updates.name = editAccountForm.name;
      }
      if (editAccountForm.plan_type !== selectedAccount.plan_type) {
        updates.plan_type = editAccountForm.plan_type;
      }
      if (editAccountForm.plan_status !== (selectedAccount.plan_status || 'active')) {
        updates.plan_status = editAccountForm.plan_status;
      }
      if (editAccountForm.is_active !== selectedAccount.is_active) {
        updates.is_active = editAccountForm.is_active;
      }

      const { success, error, account } = await updateAccount(selectedAccount.id, updates);

      if (error) {
        setError(error);
        return;
      }

      if (success && account) {
        setSuccess('Account updated successfully');
        setEditAccountMode(false);

        // Update the selected account with new data
        setSelectedAccount({
          ...selectedAccount,
          ...account
        });

        // Reload accounts list to reflect changes
        loadAccounts();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update account');
    } finally {
      setEditAccountSubmitting(false);
    }
  };

  const handleAddUserToAccount = async () => {
    if (!addUserForm.selectedUserId || !addUserForm.selectedAccountId) return;

    setAddUserSubmitting(true);
    try {
      const userToAdd = users.find(u => u.id === addUserForm.selectedUserId);
      if (!userToAdd) {
        setError('Selected user not found');
        return;
      }

      const { success, error } = await addUserToAccount(
        addUserForm.selectedAccountId,
        userToAdd.email,
        addUserForm.accountRole
      );

      if (error) {
        setError(error);
        return;
      }

      if (success) {
        const accountName = accounts.find(a => a.id === addUserForm.selectedAccountId)?.name || 'Unknown Account';
        setSuccess(`Successfully added ${userToAdd.email} to account "${accountName}" as ${addUserForm.accountRole}`);
        setAddUserToAccountDialog(false);
        setAddUserForm({
          selectedUserId: '',
          selectedAccountId: '',
          accountRole: 'member',
        });
        // Reload data to reflect changes
        loadUsers();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add user to account');
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const handleCreateUser = async () => {
    setCreateUserSubmitting(true);
    try {
      if (!createUserForm.email) {
        setError('Email is required');
        return;
      }

      // Validate account assignment for regular users
      if (createUserForm.role !== 'admin' && !createUserForm.account_id) {
        setError('Account assignment is required for regular users');
        return;
      }

      const userRequest: CreateUserRequest = {
        email: createUserForm.email,
        first_name: createUserForm.first_name || undefined,
        last_name: createUserForm.last_name || undefined,
        role: createUserForm.role,
        send_invitation: createUserForm.send_invitation,
        // Don't send password if sending invitation (user will set it themselves)
        password: createUserForm.send_invitation ? undefined : createUserForm.password,
        // Include account assignment for regular users
        account_id: createUserForm.role !== 'admin' ? createUserForm.account_id : undefined,
        account_role: createUserForm.account_role,
      };

      const { data, error } = await createUser(userRequest);

      if (error) {
        setError(error);
        return;
      }

      if (data) {
        let successMessage = `User ${createUserForm.email} created successfully`;
        if (data.invitation_sent) {
          successMessage += ' and invitation email sent';
        }
        if (data.account_assigned?.success) {
          const accountName = availableAccountsForUser.find(a => a.id === data.account_assigned?.account_id)?.name;
          successMessage += ` and assigned to account "${accountName}" as ${data.account_assigned.role}`;
        }
        setSuccess(successMessage);

        setCreateUserDialog(false);
        setCreateUserForm({
          first_name: '',
          last_name: '',
          email: '',
          role: 'user',
          password: '',
          send_invitation: true,
          account_id: '',
          account_role: 'member',
        });

        // Reload user data to show the new user
        loadUsers();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setCreateUserSubmitting(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'N/A';
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'premium': return 'primary';
      case 'pro': return 'secondary';
      case 'free': return 'default';
      default: return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'owner': return 'primary';
      default: return 'default';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
        </Box>
      </Container>
    );
  }

  // Show access denied message if user doesn't have admin privileges
  if (error?.includes('Access denied')) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Access Denied</Typography>
          You need administrator privileges to access this page. Please contact your system administrator if you believe this is an error.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Site Administration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage users and customer accounts across the entire platform
        </Typography>
      </Box>

      {/* Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && !error.includes('Access denied') && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
        gap: 3,
        mb: 4
      }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {users.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {accounts.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer Accounts
                </Typography>
              </Box>
              <BusinessIcon sx={{ fontSize: 40, color: theme.palette.success.main, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {users.filter(u => u.role === 'admin').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Site Admins
                </Typography>
              </Box>
              <AdminIcon sx={{ fontSize: 40, color: theme.palette.error.main, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {accounts.reduce((sum, acc) => sum + (acc.company_count || 0), 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Companies
                </Typography>
              </Box>
              <WarningIcon sx={{ fontSize: 40, color: theme.palette.warning.main, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card elevation={1} sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ px: 3, pt: 2 }}
          >
            <Tab
              icon={<PersonIcon />}
              label="User Management"
              iconPosition="start"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 120,
                flex: 1
              }}
            />
            <Tab
              icon={<BusinessIcon />}
              label="Account Management"
              iconPosition="start"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 120,
                flex: 1
              }}
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 0 }}>
          {loading && <LinearProgress />}

          {/* Users Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{
                mb: 3,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                minHeight: 56,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 250 }}
                  />

                  <TextField
                    select
                    label="Role"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                  </TextField>

                  <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Suspended</MenuItem>
                  </TextField>

                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={async () => {
                      setCreateUserDialog(true);
                      // Load available accounts for assignment
                      try {
                        const { accounts, error } = await getAvailableAccounts();
                        if (accounts && !error) {
                          setAvailableAccountsForUser(accounts);
                        }
                      } catch (err) {
                        console.error('Failed to load accounts:', err);
                      }
                    }}
                  >
                    Create User
                  </Button>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {filteredUsers.length} users found
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Accounts</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Sign In</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {user.first_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {user.first_name && user.last_name
                                  ? `${user.first_name} ${user.last_name}`
                                  : user.email.split('@')[0]
                                }
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.role}
                            size="small"
                            color={getRoleColor(user.role) as any}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {user.accounts.slice(0, 2).map((account) => (
                              <Chip
                                key={account.account_id}
                                label={account.account_name}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                            {user.accounts.length > 2 && (
                              <Chip
                                label={`+${user.accounts.length - 2}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.last_sign_in_at ? formatDate(user.last_sign_in_at) : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Suspended'}
                            size="small"
                            color={user.is_active ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setUserMenuAnchor(e.currentTarget);
                              setSelectedUser(user);
                            }}
                            sx={{
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Accounts Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{
                mb: 3,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                minHeight: 56,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    placeholder="Search accounts..."
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 250 }}
                  />

                  <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateAccountDialog(true)}
                  >
                    Create Account
                  </Button>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {filteredAccounts.length} accounts found
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Users</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Companies</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Records</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAccounts.map((account) => (
                      <TableRow key={account.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                              <BusinessIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {account.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {account.id.slice(0, 8)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={account.plan_type}
                            size="small"
                            color={getPlanColor(account.plan_type) as any}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>{account.user_count}</TableCell>
                        <TableCell>{account.company_count || 0}</TableCell>
                        <TableCell>{account.freight_record_count || 0}</TableCell>
                        <TableCell>{formatDate(account.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={account.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={account.is_active ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleManageAccount(account)}
                          >
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedUser) handleEditUser(selectedUser);
            setUserMenuAnchor(null);
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedUser) {
              setAddUserForm({
                selectedUserId: selectedUser.id,
                selectedAccountId: '',
                accountRole: 'member',
              });
              setAddUserToAccountDialog(true);
            }
            setUserMenuAnchor(null);
          }}
        >
          <PersonAddIcon sx={{ mr: 1 }} />
          Add to Account
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedUser) handleImpersonateUser(selectedUser);
            setUserMenuAnchor(null);
          }}
          disabled={selectedUser?.role === 'admin'}
        >
          <ImpersonateIcon sx={{ mr: 1 }} />
          Impersonate User
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedUser) handleToggleUserStatus(selectedUser);
            setUserMenuAnchor(null);
          }}
        >
          {selectedUser?.is_active ? <BlockIcon sx={{ mr: 1 }} /> : <CheckCircleIcon sx={{ mr: 1 }} />}
          {selectedUser?.is_active ? 'Suspend User' : 'Activate User'}
        </MenuItem>
      </Menu>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={editUserForm.first_name}
              onChange={(e) => setEditUserForm({ ...editUserForm, first_name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={editUserForm.last_name}
              onChange={(e) => setEditUserForm({ ...editUserForm, last_name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              value={editUserForm.email}
              disabled
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={editUserForm.role}
              onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value as 'admin' | 'user' })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={editUserForm.is_active}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onClose={() => setCreateUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Email Address *"
              type="email"
              value={createUserForm.email}
              onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
              placeholder="user@example.com"
              error={!createUserForm.email && createUserSubmitting}
              helperText={!createUserForm.email && createUserSubmitting ? 'Email is required' : ''}
            />
            <TextField
              fullWidth
              label="First Name"
              value={createUserForm.first_name}
              onChange={(e) => setCreateUserForm({ ...createUserForm, first_name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={createUserForm.last_name}
              onChange={(e) => setCreateUserForm({ ...createUserForm, last_name: e.target.value })}
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={createUserForm.role}
              onChange={(e) => {
                const newRole = e.target.value as 'admin' | 'user';
                setCreateUserForm({
                  ...createUserForm,
                  role: newRole,
                  // Clear account assignment if switching to admin
                  account_id: newRole === 'admin' ? '' : createUserForm.account_id,
                });
              }}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>

            {/* Account Assignment - Required for Regular Users */}
            {createUserForm.role !== 'admin' && (
              <>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Account Assignment Required:</strong> Regular users must be assigned to a customer account for proper access control.
                  </Typography>
                </Alert>

                <Autocomplete
                  options={availableAccountsForUser}
                  getOptionLabel={(option) => `${option.name} (${option.plan_type})`}
                  value={availableAccountsForUser.find(a => a.id === createUserForm.account_id) || null}
                  onChange={(_, newValue) => {
                    setCreateUserForm({
                      ...createUserForm,
                      account_id: newValue?.id || ''
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign to Account *"
                      placeholder="Select customer account"
                      error={!createUserForm.account_id && createUserSubmitting}
                      helperText={!createUserForm.account_id && createUserSubmitting ? 'Account assignment is required' : ''}
                    />
                  )}
                  fullWidth
                />

                <TextField
                  fullWidth
                  select
                  label="Account Role"
                  value={createUserForm.account_role}
                  onChange={(e) => setCreateUserForm({
                    ...createUserForm,
                    account_role: e.target.value as 'owner' | 'admin' | 'member'
                  })}
                  helperText="Role within the assigned account"
                >
                  <MenuItem value="member">Member - Basic access</MenuItem>
                  <MenuItem value="admin">Admin - Account management</MenuItem>
                  <MenuItem value="owner">Owner - Full control</MenuItem>
                </TextField>
              </>
            )}

            {createUserForm.role === 'admin' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Site Administrator:</strong> This user will have access to all accounts and site administration features.
                </Typography>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={createUserForm.send_invitation}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, send_invitation: e.target.checked })}
                />
              }
              label="Send email invitation to user"
            />

            {!createUserForm.send_invitation && (
              <TextField
                fullWidth
                label="Temporary Password"
                type="password"
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                placeholder="Enter a temporary password"
                helperText="User should change this password on first login"
              />
            )}


          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateUserDialog(false);
              setCreateUserForm({
                first_name: '',
                last_name: '',
                email: '',
                role: 'user',
                password: '',
                send_invitation: true,
                account_id: '',
                account_role: 'member',
              });
            }}
            disabled={createUserSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={
              createUserSubmitting ||
              !createUserForm.email ||
              (createUserForm.role !== 'admin' && !createUserForm.account_id)
            }
            startIcon={createUserSubmitting ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {createUserSubmitting ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Account Dialog with Stepper */}
      <Dialog open={createAccountDialog} onClose={() => setCreateAccountDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Customer Account</DialogTitle>
        <DialogContent>
          <Stepper activeStep={createAccountStep} orientation="vertical" sx={{ mt: 2 }}>
            <Step>
              <StepLabel>Account Details</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    value={newAccountForm.name}
                    onChange={(e) => setNewAccountForm({ ...newAccountForm, name: e.target.value })}
                    placeholder="e.g., Acme Corporation"
                  />
                  <TextField
                    fullWidth
                    select
                    label="Plan Type"
                    value={newAccountForm.plan_type}
                    onChange={(e) => setNewAccountForm({ ...newAccountForm, plan_type: e.target.value as any })}
                  >
                    <MenuItem value="starter">Starter</MenuItem>
                    <MenuItem value="essentials">Essentials</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleCreateAccountNext}
                    disabled={!newAccountForm.name}
                  >
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Assign Account Owner</StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Choose how to assign the account owner:</FormLabel>
                    <RadioGroup
                      value={newAccountForm.owner_selection}
                      onChange={(e) => setNewAccountForm({
                        ...newAccountForm,
                        owner_selection: e.target.value as 'existing' | 'new'
                      })}
                    >
                      <FormControlLabel
                        value="existing"
                        control={<Radio />}
                        label="Select existing user"
                      />
                      <FormControlLabel
                        value="new"
                        control={<Radio />}
                        label="Create new user (Coming Soon)"
                        disabled
                      />
                    </RadioGroup>
                  </FormControl>

                  {newAccountForm.owner_selection === 'existing' && (
                    <Box sx={{ mt: 2 }}>
                      <Autocomplete
                        options={availableUsers}
                        getOptionLabel={(option) => `${option.email} (${option.first_name} ${option.last_name})`}
                        value={availableUsers.find(u => u.id === newAccountForm.existing_user_id) || null}
                        onChange={(_, newValue) => {
                          setNewAccountForm({
                            ...newAccountForm,
                            existing_user_id: newValue?.id || ''
                          });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Select User" placeholder="Search by email or name" />
                        )}
                        fullWidth
                      />
                    </Box>
                  )}

                  {newAccountForm.owner_selection === 'new' && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={newAccountForm.new_user.first_name}
                        onChange={(e) => setNewAccountForm({
                          ...newAccountForm,
                          new_user: { ...newAccountForm.new_user, first_name: e.target.value }
                        })}
                      />
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={newAccountForm.new_user.last_name}
                        onChange={(e) => setNewAccountForm({
                          ...newAccountForm,
                          new_user: { ...newAccountForm.new_user, last_name: e.target.value }
                        })}
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={newAccountForm.new_user.email}
                        onChange={(e) => setNewAccountForm({
                          ...newAccountForm,
                          new_user: { ...newAccountForm.new_user, email: e.target.value }
                        })}
                      />
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={handleCreateAccountBack}>Back</Button>
                  <Button
                    variant="contained"
                    onClick={handleCreateAccountComplete}
                    disabled={
                      newAccountForm.owner_selection === 'existing'
                        ? !newAccountForm.existing_user_id
                        : !newAccountForm.new_user.email || !newAccountForm.new_user.first_name
                    }
                  >
                    Create Account
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateAccountDialog(false);
            setCreateAccountStep(0);
            setNewAccountForm({
              name: '',
              plan_type: 'starter',
              owner_selection: 'existing',
              existing_user_id: '',
              new_user: { first_name: '', last_name: '', email: '' }
            });
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Account Dialog */}
      <Dialog
        open={manageAccountDialog}
        onClose={() => {
          setManageAccountDialog(false);
          setEditAccountMode(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editAccountMode ? 'Edit Account' : 'Manage Account'}: {selectedAccount?.name}
        </DialogTitle>
        <DialogContent>
          {selectedAccount && (
            <Box sx={{ pt: 2 }}>
              {!editAccountMode && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Account ID:</strong> {selectedAccount.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created:</strong> {formatDate(selectedAccount.created_at)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Users:</strong> {selectedAccount.user_count} |
                    <strong> Companies:</strong> {selectedAccount.company_count} |
                    <strong> Records:</strong> {selectedAccount.freight_record_count}
                  </Typography>
                </Alert>
              )}

              {editAccountMode ? (
                // Edit Mode
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Changing the plan type will automatically update account limits and restrictions.
                    </Typography>
                  </Alert>

                  <TextField
                    fullWidth
                    label="Account Name"
                    value={editAccountForm.name}
                    onChange={(e) => setEditAccountForm({ ...editAccountForm, name: e.target.value })}
                    disabled={editAccountSubmitting}
                  />

                  <TextField
                    fullWidth
                    select
                    label="Plan Type"
                    value={editAccountForm.plan_type}
                    onChange={(e) => setEditAccountForm({
                      ...editAccountForm,
                      plan_type: e.target.value as 'starter' | 'essentials' | 'pro' | 'enterprise'
                    })}
                    disabled={editAccountSubmitting}
                  >
                    <MenuItem value="starter">Starter</MenuItem>
                    <MenuItem value="essentials">Essentials</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    select
                    label="Plan Status"
                    value={editAccountForm.plan_status}
                    onChange={(e) => setEditAccountForm({
                      ...editAccountForm,
                      plan_status: e.target.value as 'active' | 'canceled' | 'past_due' | 'trialing'
                    })}
                    disabled={editAccountSubmitting}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="trialing">Trialing</MenuItem>
                    <MenuItem value="past_due">Past Due</MenuItem>
                    <MenuItem value="canceled">Canceled</MenuItem>
                  </TextField>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editAccountForm.is_active}
                        onChange={(e) => setEditAccountForm({ ...editAccountForm, is_active: e.target.checked })}
                        disabled={editAccountSubmitting}
                      />
                    }
                    label="Account Active"
                  />
                </Box>
              ) : (
                // View Mode
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Account Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">
                          <strong>Name:</strong> {selectedAccount.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Plan:</strong> {selectedAccount.plan_type.charAt(0).toUpperCase() + selectedAccount.plan_type.slice(1)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Status:</strong> {selectedAccount.plan_status || 'active'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Active:</strong> {selectedAccount.is_active ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Quick Actions
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          fullWidth
                          onClick={handleEditAccount}
                        >
                          Edit Account Details
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<PersonAddIcon />}
                          fullWidth
                          onClick={() => {
                            if (selectedAccount) {
                              setAddUserForm({
                                selectedUserId: '',
                                selectedAccountId: selectedAccount.id,
                                accountRole: 'member',
                              });
                              setAddUserToAccountDialog(true);
                            }
                          }}
                        >
                          Add User to Account
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Account Analytics
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Analytics and usage metrics will be available in future updates.
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editAccountMode ? (
            <>
              <Button
                onClick={handleCancelEditAccount}
                disabled={editAccountSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAccount}
                variant="contained"
                disabled={editAccountSubmitting || !editAccountForm.name.trim()}
                startIcon={editAccountSubmitting ? <CircularProgress size={20} /> : <EditIcon />}
              >
                {editAccountSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button onClick={() => {
              setManageAccountDialog(false);
              setEditAccountMode(false);
            }}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add User to Account Dialog */}
      <Dialog open={addUserToAccountDialog} onClose={() => setAddUserToAccountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User to Account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {addUserForm.selectedUserId ? (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Selected User:</strong> {users.find(u => u.id === addUserForm.selectedUserId)?.email || 'Unknown User'}
                </Typography>
              </Alert>
            ) : (
              <Autocomplete
                options={users.filter(u => u.role !== 'admin')}
                getOptionLabel={(option) => `${option.email} ${option.first_name && option.last_name ? `(${option.first_name} ${option.last_name})` : ''}`}
                value={users.find(u => u.id === addUserForm.selectedUserId) || null}
                onChange={(_, newValue) => {
                  setAddUserForm({
                    ...addUserForm,
                    selectedUserId: newValue?.id || ''
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select User *"
                    placeholder="Search by email or name"
                    error={!addUserForm.selectedUserId && addUserSubmitting}
                    helperText={!addUserForm.selectedUserId && addUserSubmitting ? 'Please select a user' : ''}
                  />
                )}
                fullWidth
              />
            )}

            {addUserForm.selectedAccountId ? (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Target Account:</strong> {accounts.find(a => a.id === addUserForm.selectedAccountId)?.name || 'Unknown Account'}
                </Typography>
              </Alert>
            ) : (
              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => `${option.name} (${option.plan_type})`}
                value={accounts.find(a => a.id === addUserForm.selectedAccountId) || null}
                onChange={(_, newValue) => {
                  setAddUserForm({
                    ...addUserForm,
                    selectedAccountId: newValue?.id || ''
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Account *"
                    placeholder="Search accounts"
                    error={!addUserForm.selectedAccountId && addUserSubmitting}
                    helperText={!addUserForm.selectedAccountId && addUserSubmitting ? 'Please select an account' : ''}
                  />
                )}
                fullWidth
              />
            )}

            <TextField
              fullWidth
              select
              label="Account Role"
              value={addUserForm.accountRole}
              onChange={(e) => setAddUserForm({
                ...addUserForm,
                accountRole: e.target.value as 'owner' | 'admin' | 'member'
              })}
              helperText="Role within the selected account"
            >
              <MenuItem value="member">Member - Basic access</MenuItem>
              <MenuItem value="admin">Admin - Account management</MenuItem>
              <MenuItem value="owner">Owner - Full control</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddUserToAccountDialog(false);
              setAddUserForm({
                selectedUserId: '',
                selectedAccountId: '',
                accountRole: 'member',
              });
            }}
            disabled={addUserSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUserToAccount}
            variant="contained"
            disabled={
              addUserSubmitting ||
              !addUserForm.selectedUserId ||
              !addUserForm.selectedAccountId
            }
            startIcon={addUserSubmitting ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {addUserSubmitting ? 'Adding...' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SiteAdminDashboard; 